import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Message from '../../components/ui/Message';
import { costingApi, formatMoney, formatQuantity, round2 } from './costing';

const normalize = (text: any) => String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Conteo físico: se escribe lo que hay en la bodega; la diferencia contra el sistema queda como ajuste al costo
// promedio. Los productos que se dejan en blanco no se cuentan.
export const PhysicalCountPage = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<any[]>([]);
    const [counted, setCounted] = useState<Record<number, string>>({});
    const [search, setSearch] = useState('');
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [confirm, setConfirm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const load = async () => {
        setLoading(true);
        const resp = await costingApi.countSheet();
        setLoading(false);
        if (resp.data) setProducts(resp.data.products);
        else setError(resp.error!);
    };

    useEffect(() => { load(); }, []);

    const visible = useMemo(() => {
        const term = normalize(search.trim());
        return term ? products.filter((p) => normalize(`${p.code} ${p.name} ${p.category ?? ''}`).includes(term)) : products;
    }, [products, search]);

    const entered = products.filter((p) => counted[p.productId] !== undefined && counted[p.productId] !== '');
    const withDifference = entered.filter((p) => Number(counted[p.productId]) !== Number(p.systemQuantity));
    const valueDifference = round2(withDifference.reduce((sum, p) => sum + (Number(counted[p.productId]) - Number(p.systemQuantity)) * Number(p.averageCost ?? 0), 0));

    const apply = async () => {
        setConfirm(false);
        setSaving(true);
        const resp = await costingApi.applyCount({
            note,
            lines: entered.map((p) => ({ productId: p.productId, counted: Number(counted[p.productId]) })),
        });
        setSaving(false);
        if (resp.error) return setError(resp.error);
        setSuccess(`Se aplicó el conteo de ${resp.data?.counted} producto(s); ${resp.data?.differences} tenían diferencia y se ajustaron al costo promedio.`);
        setCounted({});
        setNote('');
        load();
    };

    return (
        <div className='mt-5 container-fluid px-4' style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Conteo físico</h3>
                    <p className="text-muted mb-0">Escriba la cantidad que contó de cada producto. Deje en blanco los que no contó.</p>
                </div>
                <button type="button" className="btn btn-outline-secondary mt-2 mt-md-0" onClick={() => navigate('/stocks')}>
                    <i className="bi bi-arrow-left me-1" /> Volver al inventario
                </button>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <input className="form-control" placeholder="Buscar por código, nombre o categoría..." value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Buscar producto" />
                        </div>
                        <div className="col-md-6">
                            <input className="form-control" placeholder="Nota del conteo (opcional)" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} aria-label="Nota del conteo" />
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Código</th><th>Producto</th><th>Categoría</th>
                                    <th className="text-end">En sistema</th><th style={{ width: 160 }}>Contado</th>
                                    <th className="text-end">Diferencia</th><th className="text-end">Valor de la diferencia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={7} className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" /> Cargando...</td></tr>
                                ) : visible.map((p) => {
                                    const value = counted[p.productId] ?? '';
                                    const diff = value === '' ? null : Number(value) - Number(p.systemQuantity);
                                    return (
                                        <tr key={p.productId}>
                                            <td className="text-muted">{p.code}</td>
                                            <td className="fw-medium">{p.name}</td>
                                            <td>{p.category ?? '-'}</td>
                                            <td className="text-end">{formatQuantity(p.systemQuantity)} {p.unit}</td>
                                            <td>
                                                <input aria-label={`Contado de ${p.name}`} type="number" min="0" step="any" className="form-control form-control-sm"
                                                    value={value} onChange={(e) => setCounted((prev) => ({ ...prev, [p.productId]: e.target.value }))} />
                                            </td>
                                            <td className={`text-end ${diff === null || diff === 0 ? 'text-muted' : diff > 0 ? 'text-success' : 'text-danger'}`}>
                                                {diff === null ? '-' : `${diff > 0 ? '+' : ''}${formatQuantity(diff)}`}
                                            </td>
                                            <td className="text-end">{diff ? formatMoney(diff * Number(p.averageCost ?? 0)) : '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <span className="text-muted small">
                            {entered.length} producto(s) contados · {withDifference.length} con diferencia · ajuste total {formatMoney(valueDifference)}
                        </span>
                        <button type="button" className="btn btn-primary" disabled={saving || entered.length === 0} onClick={() => setConfirm(true)}>
                            {saving ? <><span className="spinner-border spinner-border-sm me-1" /> Aplicando...</> : 'Aplicar conteo'}
                        </button>
                    </div>
                </div>
            </div>

            {confirm && (
                <Message type="info" title="Aplicar conteo" showCancel={true} okText="Aplicar"
                    message={`Se ajustarán ${withDifference.length} producto(s) a la cantidad contada, al costo promedio vigente. ¿Desea continuar?`}
                    closeDialog={() => setConfirm(false)} handleCancel={() => setConfirm(false)} handleOk={apply} backgroundColor="rgba(0,0,0,0.4)" />
            )}
            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Exitoso" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};
